import type React from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export type MobileSearchDrawerField =
  | "pickup"
  | "destination"
  | "duration"
  | "date"
  | "time"
  | "pickupDate"
  | "pickupTime"
  | "endDate"
  | "endTime";

export interface MobileSearchDrawerState<
  TField extends string = MobileSearchDrawerField,
> {
  activeField: TField | null;
}

type MobileFieldEvent =
  | React.MouseEvent<HTMLElement>
  | React.FocusEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>;

export function blurActiveElement() {
  (document.activeElement as HTMLElement | null)?.blur?.();
}

export function preventMobileFieldInteraction(event?: MobileFieldEvent) {
  event?.preventDefault();
  event?.stopPropagation();
  (event?.target as HTMLElement | null)?.blur?.();
  blurActiveElement();
}

export function closeMobileDrawerWithBlur(onClose: () => void) {
  blurActiveElement();
  onClose();
  window.setTimeout(() => {
    blurActiveElement();
  }, 60);
}

export function getMobileFieldTriggerProps<TField extends string>(
  enabled: boolean,
  field: TField,
  openField: (field: TField) => void,
) {
  if (!enabled) {
    return undefined;
  }

  const open = (event?: MobileFieldEvent) => {
    preventMobileFieldInteraction(event);
    openField(field);
  };

  return {
    readOnly: true,
    onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
      preventMobileFieldInteraction(event);
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      open(event);
    },
    onFocusCapture: (event: React.FocusEvent<HTMLElement>) => {
      open(event);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar"
      ) {
        open(event);
      }
    },
    inputProps: {
      readOnly: true,
      inputMode: "none" as const,
    },
  };
}

interface SharedMobileDrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}

export function SharedMobileFieldDrawer({
  open,
  title,
  subtitle,
  onClose,
  children,
}: SharedMobileDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => closeMobileDrawerWithBlur(onClose)}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        sx: {
          height: "100dvh",
          maxHeight: "100dvh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          width: "100%", // add this
          maxWidth: "100%", // add this
          left: 0, // add this
          right: 0, // add this
        },
      }}
      sx={{ zIndex: 1500 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #ECEBF2",
          flexShrink: 0,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography fontSize={16} fontWeight={700}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={() => closeMobileDrawerWithBlur(onClose)}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "#fff",
          width: "100%", // add this
          maxWidth: "100%", // add this
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}
